<script setup>
    import {ref, watch, onMounted, computed} from 'vue'
    import StyleSetting from "./styleSetting.vue";
    import ButtonWithConfirmation from "../buttons/buttonWithConfirmation.vue";

    const emit = defineEmits(['close', 'update:style'])

    const props = defineProps({
        styles: {
            type: Object,
            required: false,
            default: {
                mobile: [],
                desktop: [],
                app: [],
            }
        },
        defaultStyles: {
            type: Object,
            required: false,
            default: {
                mobile: [],
                desktop: [],
                app: [],
            }
        },
        colCount: {
            type: Number,
            required: false,
            default: 1
        },
        containerType: {
            type: String,
            required: false,
            default: null
        },
        whitelabelAppEnabled: {
            type: Boolean,
            required: false,
            default: false
        }
    })

    const styleFieldOrder = {
        layoutAndStructure: [
            'layout',
            'maxWidth',
            'rowMinHeight',
            'verticalAlignment',
            'rowColumnOrder',
        ],
        text: [
            'color',
        ],
        backgrounds: [
            'backgroundColor',
            'backgroundGradientStops',
            'overlay',
            'backgroundPosition',
            'backgroundMediaBankEntryId',
        ],
        spacing: [
            'margin',
            'padding',
        ]
    }

    const styleFieldGroups = {
        layoutAndStructure: {
            label: 'Layout & structure'
        },
        text: {
            label: 'Text'
        },
        backgrounds: {
            label: 'Backgrounds'
        },
        spacing: {
            label: 'Spacing'
        },
        other: {
            label: 'Other'
        }
    }

    const _styles = ref(JSON.parse(JSON.stringify(props.styles)))
    const undoStyles = ref(JSON.parse(JSON.stringify(props.styles)))

    const getOrderedStyleGroups = (styles) => {
        const styleKeys = Object.keys(styles ?? {})
        const groupedKeys = new Set()
        const orderedGroups = Object.entries(styleFieldOrder)
            .map(([groupKey, groupFields]) => {
                const keys = groupFields.filter((key) => styleKeys.includes(key))

                keys.forEach((key) => groupedKeys.add(key))

                return {
                    groupKey,
                    keys,
                }
            })
            .filter((group) => group.keys.length > 0)

        const remainingKeys = styleKeys.filter((key) => !groupedKeys.has(key))

        if (remainingKeys.length > 0) {
            orderedGroups.push({
                groupKey: 'other',
                keys: remainingKeys,
            })
        }

        return orderedGroups
    }

    const orderedDesktopStyleGroups = computed(() => getOrderedStyleGroups(_styles.value?.desktop))
    const orderedMobileStyleGroups = computed(() => getOrderedStyleGroups(_styles.value?.mobile))
    const orderedAppStyleGroups = computed(() => getOrderedStyleGroups(_styles.value?.app))

    const styleSettingLabels = {
        backgroundColor: _mft('pagebuilder:styleEditor.backgroundColor'),
        backgroundGradientStops: _mft('pagebuilder:styleEditor.backgroundGradient'),
        color: _mft('pagebuilder:styleEditor.color'),
        backgroundUrl: _mft('pagebuilder:styleEditor.backgroundUrl'),
        backgroundMediaBankEntryId: _mft('pagebuilder:styleEditor.backgroundUrl'),
        backgroundPosition: _mft('pagebuilder:styleEditor.backgroundPosition'),
        overlay: _mft('pagebuilder:styleEditor.overlay'),
        padding: _mft('pagebuilder:styleEditor.padding'),
        margin: _mft('pagebuilder:styleEditor.margin'),
        maxWidth: _mft('pagebuilder:styleEditor.maxWidth'),
        rowMinHeight: _mft('pagebuilder:styleEditor.rowMinHeight'),
        rowColumnOrder: _mft('pagebuilder:styleEditor.rowColumnOrder'),
        verticalAlignment: _mft('pagebuilder:styleEditor.verticalAlignment'),
        layout: _mft('pagebuilder:styleEditor.layout'),
    }

    const currentSection = ref('large')
    const tabSections = ref([
        {
            label: _mft('pagebuilder:styleEditor.tab.default'),
            name: 'large',
        },
        {
            label: _mft('pagebuilder:styleEditor.tab.mobile'),
            name: 'small',
        },
    ])

    const setSection = (section) => {
        currentSection.value = section.name;
    }

    const close = () => {
        emit('close');
    }

    const saveAndClose = () => {
        emit('save')
        emit('close')
    }

    const undoAndClose = () => {
        _styles.value = JSON.parse(JSON.stringify(undoStyles.value))
        emit('update:style', _styles.value)
        emit('close')
    }

    const getPresets = (attribute) => {
        if (attribute === 'maxWidth') {
            return [
                {
                    label: _mft('pagebuilder:styleEditor.setting.maxWidth.narrow'),
                    value: '512px',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.maxWidth.medium'),
                    value: '768px',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.maxWidth.wide'),
                    value: '1024px',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.maxWidth.wider'),
                    value: '1280px',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.maxWidth.fullWidth'),
                    value: '100%'
                },
            ]
        }
        if (attribute === 'rowMinHeight') {
            return [
                {
                    label: _mft('pagebuilder:styleEditor.setting.rowMinHeight.auto'),
                    value: 'auto',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.rowMinHeight.almostScreen'),
                    value: '80vh',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.rowMinHeight.fullScreen'),
                    value: '100vh',
                },
            ]
        }
        if (attribute === 'rowColumnOrder') {
            return [
                {
                    label: _mft('pagebuilder:styleEditor.setting.rowColumnOrder.default'),
                    value: '',
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.rowColumnOrder.reverse'),
                    value: 'reverse',
                },
            ]
        }
        if (attribute === 'padding') {
            return [
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.none'),
                    value: [0, 0, 0, 0],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.aLittle'),
                    value: [16, 8, 16, 8]
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.aLittleMore'),
                    value: [48, 16, 48, 16],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.decent'),
                    value: [64, 16, 64, 16],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.topAndBottom'),
                    value: [64, 0, 64, 0],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.aLotMore'),
                    value: [128, 0, 128, 0],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.spaceous'),
                    value: [192, 0, 192, 0],
                },
            ]
        }
        if (attribute === 'margin') {
            return [
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.none'),
                    value: [0, 0, 0, 0],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.aLittle'),
                    value: [16, 8, 16, 8]
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.more'),
                    value: [32, 16, 32, 16],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.topAndBottom'),
                    value: [32, 0, 32, 0],
                },
                {
                    label: _mft('pagebuilder:styleEditor.setting.margin.spaceous'),
                    value: [64, 0, 64, 0],
                },

            ]
        }

        return []
    }

    watch(() => _styles.value, (newValue) => {
        emit('update:style', newValue)
    }, {deep: true})

    onMounted(() => {
        for (const [attribute, value] of Object.entries(props.defaultStyles.app)) {
            if (!_styles.value.app) {
                _styles.value.app = {}
            }
            if (!_styles.value.app[attribute]) {
                _styles.value.app[attribute] = value
            }
        }
        for (const [attribute, value] of Object.entries(props.defaultStyles.mobile)) {
            if (!_styles.value.mobile[attribute]) {
                _styles.value.mobile[attribute] = value
            }
        }
        for (const [attribute, value] of Object.entries(props.defaultStyles.desktop)) {
            if (!_styles.value.desktop[attribute]) {
                _styles.value.desktop[attribute] = value
            }
        }

        if (props.containerType === 'app_page') {
            tabSections.value = []
            setSection({name: 'app'})
        }
    })

</script>

<template>
    <slideout-with-slots
        @close="close"
    >
        <template #title>
            <h2>{{ _mft('pagebuilder:styleEditor.title') }}</h2>
        </template>
        <template #actions>
            <div class="mb-2 flex justify-end space-x-2">
                <button-with-confirmation
                    @confirmed="undoAndClose"
                    modalClass="absolute -top-2 -right-2 p-2 bg-white rounded shadow-md z-10 text-black"
                    :buttonLabel="_mft('shared:cancel')"
                    buttonClass="text-wlPrimaryContrast"
                    :confirmButtonLabel="_mft('shared:confirm.continue')"
                    confirmButtonClass="myflow-basic-button--success whitespace-nowrap"
                    :confirm-text="_mft('pagebuilder:styleEditor.confirm.undoChanges')"
                ></button-with-confirmation>

                <button-success
                    @click="saveAndClose"
                >{{ _mft('shared:action.save') }}
                </button-success>
            </div>
        </template>
        <template #body>
            <div class="pb-24">
                <button-bar
                    :options="tabSections"
                    @select="setSection"
                />

                <div v-if="currentSection === 'large'">
                    <p class="-mt-2 text-center text-xs">
                        {{
                            _mft('pagebuilder:styleEditor.tooltip.desktopSettings')
                        }}
                    </p>
                    <ul class="-mx-4 lg:-mx-6">
                        <li v-for="group in orderedDesktopStyleGroups" :key="group.groupKey">
                            <ul class="">
                                <li class="px-4 pb-1 pt-8 lg:px-6 border-b">
                                    <h4 class="text-xs font-bold text-gray-400">{{ styleFieldGroups[group.groupKey]?.label ?? group.groupKey }}</h4>
                                </li>
                                <li
                                    v-for="styleKey in group.keys"
                                    :key="styleKey"
                                    class="border-b"
                                >
                                    <StyleSetting
                                        :styletype="styleKey"
                                        :title="styleSettingLabels[styleKey]"
                                        :presets="getPresets(styleKey)"
                                        :stylevalue="_styles.desktop[styleKey]"
                                        :col-count="colCount"
                                        @update:model-value="_styles.desktop[styleKey] = $event"
                                    />
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div v-if="currentSection === 'small'">
                    <p class="-mt-2 text-center text-xs">
                        {{
                            _mft('pagebuilder:styleEditor.tooltip.mobileSettings')
                        }}
                    </p>
                    <ul class="-mx-4 lg:-mx-6">
                        <li v-for="group in orderedMobileStyleGroups" :key="group.groupKey">
                            <ul class="">
                                <li class="px-4 pb-1 pt-8 lg:px-6 border-b">
                                    <h4 class="text-xs font-bold text-gray-400">{{ styleFieldGroups[group.groupKey]?.label ?? group.groupKey }}</h4>
                                </li>
                                <li
                                    v-for="styleKey in group.keys"
                                    :key="styleKey"
                                    class="border-b"
                                >
                                    <StyleSetting
                                        v-if="styleKey !== 'layout'"
                                        :styletype="styleKey"
                                        :title="styleSettingLabels[styleKey]"
                                        :presets="getPresets(styleKey)"
                                        :stylevalue="_styles.mobile[styleKey]"
                                        @update:model-value="_styles.mobile[styleKey] = $event"
                                    />
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>

                <div v-if="currentSection === 'app'">
                    <p class="-mt-2 text-center text-xs">
                        {{ _mft('pagebuilder:styleEditor.tooltip.appSettings') }}
                    </p>
                    <ul class="-mx-4 lg:-mx-6">
                        <li v-for="group in orderedAppStyleGroups" :key="group.groupKey">
                            <ul class="">
                                <li class="px-4 pb-1 pt-8 lg:px-6 border-b">
                                    <h4 class="text-xs font-bold text-gray-400">{{ styleFieldGroups[group.groupKey]?.label ?? group.groupKey }}</h4>
                                </li>
                                <li
                                    v-for="styleKey in group.keys"
                                    :key="styleKey"
                                    class="border-b"
                                >
                                    <StyleSetting
                                        v-if="styleKey !== 'layout'"
                                        :styletype="styleKey"
                                        :title="styleSettingLabels[styleKey]"
                                        :presets="getPresets(styleKey)"
                                        :stylevalue="_styles.app[styleKey]"
                                        @update:model-value="_styles.app[styleKey] = $event"
                                    />
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </slideout-with-slots>
</template>
